import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  IStoreConfig,
  IStoreLocation,
  ICartItem,
  ICustomerData,
  ICustomerLocationData,
  IOrderBudgetResponse,
  PriceDisplayMode,
} from '../types/storefront.types';
import type { IComboCartItem } from '../types/combo.types';
import type { OrderTypeValue } from 'sama-shared';
import type { RootState } from '../../../types/store';
import {
  isLocationOpen,
  getLocationStatusMessage,
  formatTodayHours,
  formatWeeklySchedule,
  getNextSpecialDates,
} from '../utils/storeSchedule';

export type StorefrontThemeMode = 'light' | 'dark';

export interface StorefrontState {
  subdomain: string | null;
  config: IStoreConfig | null;
  selectedLocation: IStoreLocation | null;
  orderType: OrderTypeValue;
  cart: ICartItem[];
  // Promos van aparte: merge, checkout y descuentos de `cart` se indexan por product.id.
  comboCart: IComboCartItem[];
  customer: ICustomerData | null;
  customerLocation: ICustomerLocationData | null;
  customerId: string | null;
  // Introspectivo: guarda el preview sin delivery; el envío se compone aparte.
  budget: IOrderBudgetResponse | null;
  isCartDrawerOpen: boolean;
  themeMode: StorefrontThemeMode;
}

const getInitialTheme = (): StorefrontThemeMode => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const initialState: StorefrontState = {
  subdomain: null,
  config: null,
  selectedLocation: null,
  orderType: 2,
  cart: [],
  comboCart: [],
  customer: null,
  customerLocation: null,
  customerId: null,
  budget: null,
  isCartDrawerOpen: false,
  themeMode: getInitialTheme(),
};

export const storefrontSlice = createSlice({
  name: 'storefront',
  initialState,
  reducers: {
    setSubdomain: (state, action: PayloadAction<string>) => {
      state.subdomain = action.payload;
    },
    setStoreConfig: (state, action: PayloadAction<IStoreConfig>) => {
      state.config = action.payload;
      if (!state.selectedLocation && action.payload.partner.locations.length > 0) {
        state.selectedLocation = action.payload.partner.locations[0];
      } else if (state.selectedLocation) {
        // refrescar schedule enriquecido (isOpen/todayHours/specialDates cambian con polling)
        // Solo muta si hay diff real para evitar re-render por polling sin cambios
        const fresh = action.payload.partner.locations.find(
          (l) => l.id === state.selectedLocation!.id
        );
        if (fresh) {
          const merged = { ...state.selectedLocation, ...fresh };
          const hasDiff = JSON.stringify(merged) !== JSON.stringify(state.selectedLocation);
          if (hasDiff) {
            // eslint-disable-next-line no-console
            console.debug(
              '[Storefront polling] setStoreConfig: selectedLocation actualizado por polling',
              {
                at: new Date().toISOString(),
                isOpen: fresh.isOpen,
                todayHours: (fresh as any).todayHours,
              }
            );
            state.selectedLocation = merged;
          } else {
            // eslint-disable-next-line no-console
            console.debug('[Storefront polling] setStoreConfig: sin cambios, se omite update', {
              at: new Date().toISOString(),
            });
          }
        }
      }
    },
    refreshSelectedLocationSchedule: (
      state,
      action: PayloadAction<{
        isOpen?: boolean;
        todayHours?: string | null;
        scheduleReason?: string | null;
      }>
    ) => {
      if (state.selectedLocation) {
        state.selectedLocation = { ...state.selectedLocation, ...action.payload };
      }
    },
    setSelectedLocation: (state, action: PayloadAction<IStoreLocation>) => {
      state.selectedLocation = action.payload;
      state.cart = [];
      state.comboCart = [];
    },
    setOrderType: (state, action: PayloadAction<OrderTypeValue>) => {
      state.orderType = action.payload;
    },
    addToCart: (state, action: PayloadAction<ICartItem>) => {
      const existingIndex = state.cart.findIndex(
        (item) =>
          item.product.id === action.payload.product.id &&
          JSON.stringify(item.selectedModifiers) ===
            JSON.stringify(action.payload.selectedModifiers)
      );

      if (existingIndex >= 0) {
        const existing = state.cart[existingIndex];
        const newQty = existing.quantity + action.payload.quantity;
        existing.quantity = newQty;
        existing.totalPrice = existing.unitPrice * newQty;
      } else {
        state.cart.push(action.payload);
      }
    },
    updateCartItemQuantity: (
      state,
      action: PayloadAction<{ cartItemId: string; quantity: number }>
    ) => {
      const item = state.cart.find((i) => i.cartItemId === action.payload.cartItemId);
      if (item) {
        if (action.payload.quantity <= 0) {
          state.cart = state.cart.filter((i) => i.cartItemId !== action.payload.cartItemId);
        } else {
          item.quantity = action.payload.quantity;
          item.totalPrice = item.unitPrice * action.payload.quantity;
        }
      }
    },
    removeCartItem: (state, action: PayloadAction<string>) => {
      state.cart = state.cart.filter((i) => i.cartItemId !== action.payload);
    },
    addComboToCart: (state, action: PayloadAction<IComboCartItem>) => {
      const existing = state.comboCart.find(
        (item) =>
          item.combo.id === action.payload.combo.id &&
          JSON.stringify(item.selections) === JSON.stringify(action.payload.selections)
      );
      if (existing) {
        existing.quantity += action.payload.quantity;
        existing.totalPrice = existing.unitPrice * existing.quantity;
      } else {
        state.comboCart.push(action.payload);
      }
    },
    updateComboCartItemQuantity: (
      state,
      action: PayloadAction<{ cartItemId: string; quantity: number }>
    ) => {
      const { cartItemId, quantity } = action.payload;
      const item = state.comboCart.find((i) => i.cartItemId === cartItemId);
      if (!item) return;
      if (quantity <= 0) {
        state.comboCart = state.comboCart.filter((i) => i.cartItemId !== cartItemId);
      } else {
        item.quantity = quantity;
        item.totalPrice = item.unitPrice * quantity;
      }
    },
    removeComboCartItem: (state, action: PayloadAction<string>) => {
      state.comboCart = state.comboCart.filter((i) => i.cartItemId !== action.payload);
    },
    clearCart: (state) => {
      state.cart = [];
      state.comboCart = [];
      state.budget = null;
    },
    setCustomer: (state, action: PayloadAction<ICustomerData>) => {
      state.customer = action.payload;
    },
    setCustomerLocation: (state, action: PayloadAction<ICustomerLocationData>) => {
      state.customerLocation = action.payload;
    },
    setCustomerId: (state, action: PayloadAction<string | null>) => {
      state.customerId = action.payload;
    },
    setBudget: (state, action: PayloadAction<IOrderBudgetResponse | null>) => {
      state.budget = action.payload;
    },
    setCartDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.isCartDrawerOpen = action.payload;
    },
    setThemeMode: (state, action: PayloadAction<StorefrontThemeMode>) => {
      state.themeMode = action.payload;
    },
    toggleTheme: (state) => {
      state.themeMode = state.themeMode === 'light' ? 'dark' : 'light';
    },
  },
});

export const {
  setSubdomain,
  setStoreConfig,
  setSelectedLocation,
  setOrderType,
  addToCart,
  updateCartItemQuantity,
  removeCartItem,
  addComboToCart,
  updateComboCartItemQuantity,
  removeComboCartItem,
  clearCart,
  setCustomer,
  setCustomerLocation,
  setCustomerId,
  setBudget,
  setCartDrawerOpen,
  setThemeMode,
  toggleTheme,
  refreshSelectedLocationSchedule,
} = storefrontSlice.actions;

export const selectConfig = (state: RootState) => state.storefront.config;
export const selectSubdomain = (state: RootState) => state.storefront.subdomain;
export const selectCart = (state: RootState) => state.storefront.cart;
export const selectComboCart = (state: RootState): IComboCartItem[] => state.storefront.comboCart;
export const selectCartTotalElements = (state: RootState) =>
  [...state.storefront.cart, ...state.storefront.comboCart].reduce(
    (total: number, item: { quantity: number }) => total + item.quantity,
    0
  );
export const selectIsCartDrawerOpen = (state: RootState) => state.storefront.isCartDrawerOpen;
export const selectSelectedLocation = (state: RootState) => state.storefront.selectedLocation;
export const selectCustomer = (state: RootState) => state.storefront.customer;
export const selectCustomerLocation = (state: RootState) => state.storefront.customerLocation;
export const selectCustomerId = (state: RootState) => state.storefront.customerId;
export const selectBudget = (state: RootState) => state.storefront.budget;
export const selectExchangeRate = (state: RootState) => state.storefront.config?.exchangeRate;
export const selectPriceDisplayMode = (state: RootState): PriceDisplayMode =>
  state.storefront.config?.settings?.effectivePriceDisplayMode ?? 'BOTH';
/** Sin tasa activa el backend rechaza órdenes: bloquear checkout. */
export const selectCanOrder = (state: RootState): boolean =>
  !!state.storefront.config?.exchangeRate;
export const selectThemeMode = (state: RootState) => state.storefront.themeMode;

export const selectIsStoreOpen = (state: RootState): boolean => {
  const location = state.storefront.selectedLocation;
  if (!location) return false;
  return isLocationOpen(location);
};

/**
 * Los selectores que derivan objetos o arrays van memoizados con `createSelector`.
 * Sin memoizar devuelven una referencia nueva en cada llamada y `useSelector`,
 * que compara por identidad, re-renderiza con cualquier accion del store.
 *
 * La frescura del horario la garantiza `useStoreScheduleSync`: el polling
 * actualiza `selectedLocation` y con eso invalida la memo.
 */
export const selectStoreStatus = createSelector(
  [selectSelectedLocation],
  (location): { isOpen: boolean; message: string } =>
    location
      ? getLocationStatusMessage(location)
      : { isOpen: false, message: 'No hay sucursal seleccionada' }
);

export const selectSelectedLocationSchedule = createSelector([selectSelectedLocation], (loc) =>
  loc
    ? {
        isOpen: isLocationOpen(loc),
        status: getLocationStatusMessage(loc),
        todayHours: formatTodayHours(loc),
        weekly: formatWeeklySchedule(loc.operatingSchedules),
        nextSpecialDates: getNextSpecialDates(loc.specialDates, 5),
        timezone: loc.timezone,
        scheduleReason: loc.scheduleReason ?? null,
      }
    : null
);

export const selectAllLocationsWithStatus = createSelector(
  [(state: RootState) => state.storefront.config?.partner.locations],
  (locations) =>
    (locations || []).map((loc: IStoreLocation) => ({
      ...loc,
      computedIsOpen: isLocationOpen(loc),
      status: getLocationStatusMessage(loc),
      todayHours: formatTodayHours(loc),
    }))
);

export default storefrontSlice.reducer;
